import { RecycleViewElementsState } from '@nomad2102npm/cdk/collections'
import { transferArrayItem } from '@nomad2102npm/cdk/drag-drop'
import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { Component, OnInit, inject, signal, computed } from '@angular/core'
import { RecordActionPanelService } from '@app/feature/record-actions-panel/record-action-panel.service'
import { RecordCardNavigationService } from '@app/pages/service/record-card-navigation.service'
import { UiRecordsSizingService } from '@app/views/board/ui-records-sizing.service'
import { BoardCardMenuComponent } from '@app/views/board/board-card-menu/board-card-menu.component'
import { BoardColumnHeaderMenuComponent } from '@app/views/board/board-column-header-menu/board-column-header-menu.component'
import { ViewDataService } from '@app/views/services/view-data.service'
import { ViewValidatorService } from '@app/views/services/view-validator.service'
import { ViewGroupService, ViewInvalidMessageComponent } from '@app/views/view-controls'
import { FilterNoResultsComponent } from '@app/views/view-controls/view-filter/filter-no-results/filter-no-results.component'
import { TbBoardComponent } from '@components-library/tb-board/tb-board.component'
import { TbBoardCardComponent } from '@components-library/tb-board/tb-board-card/tb-board-card.component'
import { TbBoardColumnComponent } from '@components-library/tb-board/tb-board-column/tb-board-column.component'
import { isBoardCardData } from '@components-library/tb-board/board-utils'
import { TbMovedItem } from '@components-library/tb-board/board.model'
import { TbDragDropMonitorService } from '@components-library/tb-drag-and-drop/tb-drag-drop-monitor.service'
import { TbIconToggleButtonComponent } from '@components-library/tb-icon-toggle-button/tb-icon-toggle-button.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { ModalContainerTypes } from '@components-library/tb-modal-manager/modal-manager.service'
import { ScrollingModule } from '@nomad2102npm/cdk/scrolling'
import { NO_GROUPED_RECORDS_KEY } from '@core/@ngrx'
import { isNonNull } from '@core/global-util'
import {
    BaseMainBusinessRecord,
    CellEntities,
    CurrentUser,
    Field,
    FieldEntities,
    FieldTypes,
    groupValue,
    MainBusinessRecord,
    RecordGroup,
    ViewData,
    Workspace,
} from '@core/models'
import { RecordsService } from '@core/services/records.service'
import {
    FieldTypeFacadeService,
    RecordFacadeService,
    UserFacadeService,
    FieldFacadeService,
    WorkspaceFacadeService,
} from '@core/services/store-facade'
import { TranslocoModule } from '@jsverse/transloco'
import { SortDirection } from '@models/ui/sort.model'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import {
    CollapseState,
    CollapseStateStorageService,
} from '@services/local-storage/collapse-state-storage.service'
import { TbNotificationService } from '@components-library/tb-notification/tb-notification.service'
import { UiFacadeService } from '@services/store-facade/ui-facade.service'
import { flatMap, keyBy, mapValues, omitBy, sum } from 'lodash-es'
import { filter, Observable, of, combineLatest } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

import { GroupingValuesComponent } from '@shared/grouping-values/grouping-values.component'
import { BoardCardFieldsComponent } from './board-card-fields/board-card-fields.component'

@UntilDestroy()
@Component({
    selector: 'app-board-view',
    templateUrl: './board-view.component.html',
    styleUrls: ['./board-view.component.sass'],
    imports: [
        TbBoardComponent,
        TbBoardColumnComponent,
        GroupingValuesComponent,
        TranslocoModule,
        BoardCardFieldsComponent,
        TbBoardCardComponent,
        NgTemplateOutlet,
        AsyncPipe,
        BoardCardMenuComponent,
        BoardColumnHeaderMenuComponent,
        ViewInvalidMessageComponent,
        FilterNoResultsComponent,
        TbIconToggleButtonComponent,
        ModalLayoutComponent,
        ScrollingModule,
    ],
    providers: [RecycleViewElementsState],
})
export class BoardViewComponent implements OnInit {
    private readonly COLLAPSED_COLUMN_WIDTH = 56
    private readonly EXPANDED_COLUMN_WIDTH = 332

    private readonly recordService = inject(RecordsService)
    private readonly fieldTypeFacadeService = inject(FieldTypeFacadeService)
    private readonly fieldFacadeService = inject(FieldFacadeService)
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    private readonly recordFacadeService = inject(RecordFacadeService)
    private readonly notificationService = inject(TbNotificationService)
    private readonly uiFacadeService = inject(UiFacadeService)
    private readonly viewDataService = inject(ViewDataService)
    private readonly viewGroupService = inject(ViewGroupService)
    private readonly actionPanelService = inject(RecordActionPanelService)
    private readonly viewValidatorService = inject(ViewValidatorService)
    private readonly collapseStateStorageService = inject(CollapseStateStorageService)
    private readonly userFacadeService = inject(UserFacadeService)
    private readonly recordCardNavigationService = inject(RecordCardNavigationService)
    private readonly uiRecordsSizingService = inject(UiRecordsSizingService)
    private readonly dragDropMonitor = inject(TbDragDropMonitorService)

    protected readonly ModalContainer = ModalContainerTypes
    protected readonly NO_GROUPED_RECORDS_KEY = NO_GROUPED_RECORDS_KEY
    private groupDirection!: SortDirection

    viewData!: ViewData
    visibleFields: string[] = []
    fields!: FieldEntities
    groups!: RecordGroup[] | undefined
    groupsColumHeight!: number[][]
    cells!: { [recordGuid: string]: CellEntities }

    selectedWorkspace$: Observable<Workspace> =
        this.workspaceFacadeService.selectSelectedWorkspace$.pipe(filter(isNonNull))
    fieldTypes$ = this.fieldTypeFacadeService.selectFieldTypeEntities$

    boardItemsKey: keyof BaseMainBusinessRecord = 'guid'
    allRecords: Dictionary<MainBusinessRecord> = {}

    fields$: Observable<FieldEntities> = this.fieldFacadeService
        .selectSelectedTableSchemaFieldEntitiesFiltered$()
        .pipe(
            filter(isNonNull),
            map((fields) => {
                return omitBy(fields, (field) => field.field_type_code === FieldTypes.NAME)
            }),
        )

    selectedRecords: MainBusinessRecord[] = []
    collapseState: CollapseState = {}
    currentUser!: CurrentUser

    hasFieldErrors$ = this.viewValidatorService.validateViewErrors().pipe(untilDestroyed(this))

    filterHasNoResults = false

    watchByRecords!: { [recordGuid: string]: boolean }

    private readonly scrollingStore = inject(RecycleViewElementsState)

    columnWidths = signal<number[]>([])
    fullWidth = computed(() => sum(this.columnWidths()) + 'px')

    readonly groupValue = groupValue

    constructor() {
        const recordFacadeService = this.recordFacadeService
        const actionPanelService = this.actionPanelService

        actionPanelService
            .getSelectedRecords$()
            .pipe(untilDestroyed(this))
            .subscribe((records) => {
                this.selectedRecords = records
            })

        recordFacadeService
            .selectRecordEntities$()
            .pipe(untilDestroyed(this))
            .subscribe((records) => {
                this.allRecords = records
            })

        this.fields$.subscribe((fields) => (this.fields = fields))

        this.dragDropMonitor.onDrop$.pipe(untilDestroyed(this)).subscribe(({ source }) => {
            if (!isBoardCardData<BaseMainBusinessRecord>(source.data)) return

            const item = source.data.boardCardData
            if (this.scrollingStore.has(item.guid)) {
                this.scrollingStore.remove(item.guid)
            }
        })
    }

    ngOnInit(): void {
        this.viewDataService.viewDataChanges$
            .pipe(
                untilDestroyed(this),
                switchMap(() => {
                    const viewData = this.viewDataService.getViewData()

                    return combineLatest([
                        this.getViewDataValue$(viewData),
                        this.userFacadeService.currentUser$,
                        this.viewValidatorService.filterHasNoResults(
                            viewData.selected_view!,
                            this.viewDataService.isViewEmpty(),
                        ),
                        this.viewGroupService.getGroupByView$(viewData.selected_view!),
                    ])
                }),
            )
            .subscribe(([viewData, currentUser, filterHasNoResults, groupObject]) => {
                if (!viewData.selected_view || !currentUser) return

                this.viewData = viewData

                this.groupDirection = groupObject.sortDirection!

                this.groups = this.getGroups()

                this.collapseState =
                    this.collapseStateStorageService.get(this.viewData.selected_view!.guid) ?? {}

                this.cells = this.viewDataService.getCells()
                this.visibleFields = [...this.viewData.columns.columns]

                this.filterHasNoResults = filterHasNoResults

                this.currentUser = currentUser

                this.groupsColumHeight =
                    this.groups?.map((group) => {
                        return this.uiRecordsSizingService.getBoardCardsSizes(
                            group.data,
                            this.fields,
                            this.visibleFields,
                        )
                    }) ?? []

                this.setWatchByRecords()
                this.updateCollapseState()
            })
    }

    isWatchVisible(record: BaseMainBusinessRecord) {
        return this.visibleFields.find((fieldGuid) => record.watch.field_guid === fieldGuid)
    }

    drop(group: RecordGroup, movedItem: TbMovedItem<MainBusinessRecord>) {
        if (group.field && group.value) {
            this.moveCardToAnotherColumn(group.field, movedItem, group.value)
        }
    }

    openCardDialog(record: MainBusinessRecord) {
        this.recordCardNavigationService.openRecordCard({
            recordGuid: record.guid,
            isWorkspace: true,
        })
    }

    toggleSelection(record: MainBusinessRecord) {
        this.actionPanelService.toggle(record)
    }

    toggleGroup(selected: boolean, group: RecordGroup) {
        if (selected) {
            this.actionPanelService.selectRecords(group.data)
        } else {
            this.actionPanelService.deselectRecords(group.data)
        }
    }

    isRecordSelected(record: MainBusinessRecord) {
        return this.actionPanelService.isSelected(record)
    }

    openFiltersMenu() {
        this.uiFacadeService.openViewFilterDialog()
    }

    getCollapseState(value?: string, guid?: string) {
        if (!guid || !value || !this.collapseState) return false

        if (this.isCollapsedNotSet(value, guid)) return false

        return this.collapseState[guid][value]
    }

    isCollapsedNotSet(value?: string, guid?: string) {
        if (!guid || !value || !this.collapseState) return true

        const collapseFieldObject = this.collapseState[guid]
        if (!collapseFieldObject) return true

        return collapseFieldObject[value] === undefined
    }

    setCollapseState(collapseState: boolean, value?: string, guid?: string) {
        if (!guid || !value || !this.viewData.selected_view) return

        if (!this.collapseState[guid]) {
            this.collapseState[guid] = {}
        }

        this.collapseState[guid][value] = collapseState

        this.collapseStateStorageService.set(this.viewData.selected_view.guid, this.collapseState)

        this.updateCollapseState()
    }

    updateWatch(record: MainBusinessRecord) {
        this.recordService.toggleFavorite(record, this.currentUser.guid).subscribe()
    }

    trackBy(index: number, item: RecordGroup) {
        return item.value
    }

    private updateCollapseState() {
        if (!this.groups) return

        const widths = this.groups.map((group) => {
            const guid = group.field?.guid
            const value = group.value

            const isCollapsed = guid && value && this.collapseState?.[guid]?.[value]

            return (isCollapsed ?? group.data.length === 0)
                ? this.COLLAPSED_COLUMN_WIDTH
                : this.EXPANDED_COLUMN_WIDTH
        })

        this.columnWidths.set(widths)
    }

    private getViewDataValue$(viewData: ViewData) {
        if (!viewData || viewData.data instanceof Map) return of(viewData)

        return this.changeArrayDataToGroup(viewData)
    }

    private moveCardToAnotherColumn(
        field: Field,
        item: TbMovedItem<MainBusinessRecord>,
        value: string,
    ) {
        // at this moment we do not support reordering #2896
        if (item.previousItems === item.items) return

        const moveCard = (item.previousItems as BaseMainBusinessRecord[]).find(
            (record) => record.guid === item.item.guid,
        )

        if (!moveCard) return

        if (this.isNoGroupedStatus(field.field_type_code, value)) {
            this.notificationService.openError('notification_messages.no_status_group_error')
            return
        }

        transferArrayItem(item.previousItems, item.items, item.previousIndex, item.currentIndex)

        this.recordService
            .updateRecord({
                record: moveCard!,
                cell: this.cells[moveCard.guid][field.guid],
                value: value !== NO_GROUPED_RECORDS_KEY ? value : '',
            })
            .subscribe()
    }

    private changeArrayDataToGroup(viewData: ViewData) {
        return this.workspaceFacadeService.selectSelectedWorkspaceStatusField$.pipe(
            switchMap((workspaceStatus) => {
                if (workspaceStatus) return of(workspaceStatus)

                return this.workspaceFacadeService.selectGlobalWorkspaceStatus$
            }),
            map((workspaceStatus) => {
                if (!workspaceStatus || !viewData) return viewData

                return this.changeViewData(workspaceStatus, viewData)
            }),
        )
    }

    private changeViewData(workspaceStatus: Field, viewData: ViewData) {
        const data = this.recordFacadeService.generateGroup(
            viewData.data as MainBusinessRecord[],
            workspaceStatus.guid,
            workspaceStatus,
        )

        return {
            ...viewData,
            data: data,
        }
    }

    private isNoGroupedStatus(fieldType: string, value: string) {
        return fieldType === 'field_type_status' && value === NO_GROUPED_RECORDS_KEY
    }

    private getGroups() {
        if (this.viewData && this.viewData.data instanceof Map) {
            if (this.groupDirection === SortDirection.DESC) return [...this.viewData.data.values()]

            return [...this.viewData.data.values()].reverse()
        }
        return
    }

    private setWatchByRecords() {
        this.watchByRecords = {}
        this.watchByRecords = mapValues(keyBy(flatMap(this.groups, 'data'), 'guid'), (record) =>
            this.recordService.isFavorite(record.watch.value, this.currentUser.guid),
        )
    }
}
